import { useCallback, useMemo, useState } from 'react';

import { useUserProfileData } from '@/hooks/useUserProfileData';
import { Button } from '@/lib/base/buttons/button';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';

import ManageCannedResponsesModal from './ManageCannedResponsesModal';
import type { CannedResponseWithSource } from './useCannedResponses';
import { useCannedResponses } from './useCannedResponses';

export interface CannedResponsesMenuProps {
  orgId: number;
  /** The email body currently in the editor (html). */
  currentBody: string;
  /** Replace the email body with the selected response's html. */
  onInsert: (html: string) => void;
}

const MANAGE_ACTION_ID = '__manage__';

// Elements that render something a user would recognise as their draft even
// when the body carries no text at all (an inline image pasted into an
// otherwise-empty compose window is the common case).
const NON_TEXT_CONTENT_SELECTOR =
  'img, picture, video, audio, iframe, embed, object, svg, canvas, table, hr';

function bodyHasContent(html: string): boolean {
  // Parse into an inert document rather than using a single-pass tag-stripping
  // regex (which is incomplete: `<scr<script>ipt>` survives one pass).
  // DOMParser does not execute scripts or fetch resources.
  const { body } = new DOMParser().parseFromString(html, 'text/html');
  if ((body.textContent ?? '').trim().length > 0) {
    return true;
  }
  // Text-free but not empty: image-only or structure-only drafts still count,
  // so replacing them goes through the confirmation path.
  return body.querySelector(NON_TEXT_CONTENT_SELECTOR) !== null;
}

/**
 * "Canned responses" dropdown rendered above the email body editor.
 *
 * Selecting a response replaces the email content (with a confirmation when
 * the body is non-empty). The menu also opens the management modal. Renders
 * nothing when the user has no email integrations connected.
 */
export default function CannedResponsesMenu({
  orgId,
  currentBody,
  onInsert,
}: CannedResponsesMenuProps) {
  const { data: profileData } = useUserProfileData();
  const userId = profileData?.user?.id;
  const data = useCannedResponses(orgId);
  const { responses, primaryEntityId } = data;

  const [isManageOpen, setIsManageOpen] = useState(false);
  const [pendingInsert, setPendingInsert] =
    useState<CannedResponseWithSource | null>(null);

  const responsesById = useMemo(() => {
    const map = new Map<string, CannedResponseWithSource>();
    for (const response of responses) {
      map.set(`${response.entityId}:${response.id}`, response);
    }
    return map;
  }, [responses]);

  const handleAction = useCallback(
    (key: string | number) => {
      if (key === MANAGE_ACTION_ID) {
        setIsManageOpen(true);
        return;
      }
      const response = responsesById.get(String(key));
      if (!response) {
        return;
      }
      if (bodyHasContent(currentBody)) {
        setPendingInsert(response);
        return;
      }
      onInsert(response.html);
    },
    [responsesById, currentBody, onInsert]
  );

  const handleConfirmInsert = useCallback(() => {
    if (pendingInsert) {
      onInsert(pendingInsert.html);
    }
    setPendingInsert(null);
  }, [pendingInsert, onInsert]);

  const handleDismissInsert = useCallback(() => setPendingInsert(null), []);

  const handleConfirmOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setPendingInsert(null);
    }
  }, []);

  // No connected email integration (or user still loading) — nothing to offer.
  if (!userId || !primaryEntityId) {
    return null;
  }

  return (
    <>
      <Dropdown.Root>
        <Button color='tertiary' size='sm'>
          Templates
        </Button>
        <Dropdown.Popover>
          <Dropdown.Menu onAction={handleAction}>
            {responses.map(response => (
              <Dropdown.Item
                key={`${response.entityId}:${response.id}`}
                id={`${response.entityId}:${response.id}`}
                label={response.name}
              />
            ))}
            {responses.length > 0 && <Dropdown.Separator />}
            <Dropdown.Item id={MANAGE_ACTION_ID} label='Manage templates…' />
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>

      <ManageCannedResponsesModal
        orgId={orgId}
        userId={userId}
        isOpen={isManageOpen}
        onOpenChange={setIsManageOpen}
        data={data}
      />

      <ModalOverlay
        isOpen={pendingInsert !== null}
        onOpenChange={handleConfirmOpenChange}
      >
        <Modal className='max-w-md'>
          <Dialog
            aria-label='Replace email content'
            title='Replace email content?'
            description={
              pendingInsert
                ? `Using “${pendingInsert.name}” will replace what you've written so far. You can undo this right after.`
                : ''
            }
            footer={
              <div className='flex justify-end gap-3'>
                <Button
                  color='secondary'
                  size='sm'
                  onClick={handleDismissInsert}
                >
                  Cancel
                </Button>
                <Button color='primary' size='sm' onClick={handleConfirmInsert}>
                  Replace content
                </Button>
              </div>
            }
          >
            <p className='text-xs text-[var(--color-text-tertiary)]'>
              Your current draft text will be discarded.
            </p>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  );
}
